// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Button } from '@mui/material';
import { type AssetStock } from '@shared/data-access/interfaces/api/brokerage/assets';
import { messageHighMape } from '@advisor-app/features/brokers/utils/message-high-mape';
import { alertUnavailableTrades } from '@advisor-app/features/operations/utils/alert-unavailable-trades';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit/components/popups/show-modal';
import { alertInsufficientCash } from '@advisor-app/features/utils/alert-insufficient-cash';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type TradeDetails } from '@shared/data-access/interfaces/redux/trades';
import { validateOpenMarket } from '@shared/ui-kit/utils/validate-open-market';
import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  selectBrokerApiParams,
  setOpenLinkAccountModal,
} from '@advisor-app/store/slices/broker';
import { selectValidTrades } from '@advisor-app/store/slices/trader';
import Swal from 'sweetalert2';
import { useExecuteTrades } from '../hooks/use-execute-trades';
import { getAssetsExchange, getLatestPrices } from '../utils';
import { OrdersStatus } from './orders-status';
import { useTranslation } from 'react-i18next';

export type ButtonExecuteTradesProps = {
  data: TradeDetails[];
  mape: number;
  areSomeTradeUnavailable: boolean;
  container?: boolean;
  debt?: number;
};

export const ButtonExecuteTrades = ({
  mape,
  areSomeTradeUnavailable,
  container = true,
  debt = 0,
}: ButtonExecuteTradesProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const broker = useAppSelector(selectBrokerApiParams);

  const validTrades = useAppSelector(selectValidTrades);

  const { open, closeModal, executeTrades, orders, loading } =
    useExecuteTrades(validTrades);

  const isDisabled = areSomeTradeUnavailable || validTrades.length === 0;

  const openModal = useCallback(
    () => dispatch(setOpenLinkAccountModal(true)),
    [dispatch],
  );

  const updateModalTable = useCallback(
    (obj: AssetStock[], prices: Record<string, number>) => {
      return showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).EXECUTE_TRADES,
        content: (
          <div id="tradeExecutionTable" className="!mt-0 max-h-72 overflow-y-auto">
            <table className="table-modal mx-auto mt-2 table-auto">
              <thead className="sticky top-0 bg-white">
                <tr>
                  <th>{t('brokers.ticker', { ns: 'components' })}</th>
                  <th>{t('brokers.qty', { ns: 'components' })}</th>
                  <th>{t('brokers.estimated-price', { ns: 'components' })}</th>
                  <th>{t('brokers.investment', { ns: 'components' })}</th>
                  <th>{t('brokers.market', { ns: 'components' })}</th>
                  <th>{t('brokers.type', { ns: 'components' })}</th>
                </tr>
              </thead>
              <tbody>
                {validTrades.map((trade, index) => {
                  const price = prices[trade.ticker];
                  const investment = price * trade.contracts_number;
                  return (
                    <tr key={index}>
                      <td className="flex">
                        <div className="mr-2 h-6 w-6 overflow-hidden rounded-full">
                          <img
                            src={trade.columnName?.url_logo}
                            className="h-full w-full object-scale-down"
                            alt={`${trade.ticker} logo`}
                          />
                        </div>
                        <div className="flex items-center">{trade.ticker}</div>
                      </td>
                      <td className="!money-display">{trade.contracts_number}</td>
                      <td className="!money-display">{formatToMoney(price)}</td>
                      <td className="!money-display">
                        {formatToMoney(investment)}
                      </td>
                      <td>{obj[index]?.market}</td>
                      <td>{t('glossary.at-market', { ns: 'glossary' })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ),
        width: '580px',
        buttons: {
          cancel: {
            show: true,
          },
          confirm: {
            show: true,
            text: t('brokers.execute', { ns: 'components' }),
          },
        },
      });
    },
    [t, validTrades],
  );

  const handleExecuteTrades = useCallback(async () => {
    if (!broker.brokerConnected) {
      openModal();
      return;
    }

    if (debt > 0) {
      const { isConfirmed } = await alertInsufficientCash(debt, t);

      if (!isConfirmed) return;
    }

    const isMarketOpen = validateOpenMarket();

    if (!isMarketOpen) {
      return showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).WARN,
        title: t('brokers.market-closed', '', { ns: 'components' }),
        content: t('brokers.market-hours', '', { ns: 'components' }),
        buttons: {
          confirm: {
            show: true,
            text: t('common.ok', '', { ns: 'common' }),
          },
        },
      } as ShowModalOptions);
    }

    if (validTrades.length > 0) {
      showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).EXECUTE_TRADES,
        content: t('brokers.getting-market-exchange', { ns: 'components' }),
        width: '475px',
        didOpen: async () => {
          Swal.showLoading();
          const tickers = validTrades.map(trade => trade.ticker);
          const brokerConf = {
            broker_key: broker.brokerConnected,
            type_account: broker.accountTypeSelect,
          };
          const [prices, assetsExchange] = await Promise.all([
            getLatestPrices(broker, tickers, dispatch),
            getAssetsExchange(brokerConf, tickers, dispatch),
          ]);

          const pricesObj = prices.reduce((acc, curr) => {
            acc[curr.ticker] = curr.price;
            return acc;
          }, {} as Record<string, number>);
          /* istanbul ignore else */
          if (assetsExchange.success && assetsExchange.data) {
            const { isConfirmed } = await updateModalTable(
              assetsExchange.data,
              pricesObj,
            );

            if (!isConfirmed) return;
            executeTrades(prices);
          } else {
            Swal.close();
            showModal({
              ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
              content: t('brokers.error-market-exchange', { ns: 'components' }),
              width: '400px',
              buttons: {
                confirm: {
                  show: true,
                  text: t('common.ok', { ns: 'common' }),
                },
              },
            });
          }
        },
      } as ShowModalOptions);
    }
  }, [
    broker,
    debt,
    validTrades,
    openModal,
    t,
    dispatch,
    updateModalTable,
    executeTrades,
  ]);

  useEffect(() => {
    if (mape > 5) {
      const timeout = setTimeout(() => {
        messageHighMape(mape, t);
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [mape, t]);

  useEffect(() => {
    if (areSomeTradeUnavailable) {
      const timeout = setTimeout(() => {
        alertUnavailableTrades(t);
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [areSomeTradeUnavailable, t]);

  return (
    <>
      {container ? (
        <div className="inline">
          <Button
            color="secondary"
            onClick={handleExecuteTrades}
            data-testid="test-execute-trades"
            disabled={isDisabled}
          >
            {t('brokers.execute-trades', { ns: 'components' })}
          </Button>
        </div>
      ) : (
        <Button
          color="secondary"
          onClick={handleExecuteTrades}
          data-testid="test-execute-trades"
          disabled={isDisabled}
        >
          {t('brokers.execute-trades', { ns: 'components' })}
        </Button>
      )}

      <OrdersStatus
        open={open}
        handleClose={closeModal}
        orders={orders}
        loading={loading}
      />
    </>
  );
};
